import * as ng from 'angular';
import * as _ from 'underscore';

const storePrefix = 'filter.service.';

ng.module('e360')
   .factory('filterServiceFactory', filterServiceFactory);

ng.module('e360')
   .factory('filterReset', filterResetFactory);

filterServiceFactory.$inject = ['instanceServiceFactory'];
function filterServiceFactory(instanceServiceFactory) {
   return instanceServiceFactory(FilterService);
}

filterResetFactory.$inject = ['localStorageService'];
function filterResetFactory(localStorageService) {
   return filterReset;
   function filterReset() {
      for (let key of localStorageService.keys()) {
         if (key.indexOf(storePrefix) == 0) {
            localStorageService.remove(key);
         }
      }
   }
}

export class FilterService {
   static $inject = ['localStorageService', 'key', 'defaults', 'properties', 'callback'];

   private _inputs:{};

   constructor(
      private localStorageService,
      private key:string,
      private defaults:{},
      properties:string[],
      private callback:Function
   ) {
      this.key = storePrefix + key;

      // clone defaults so that mutating inputs does not change defaults
      this._inputs = localStorageService.get(this.key) || _.clone(this.defaults);

      // define getter/setter for all available filter properties
      // this is so views can bind to a filter, e.g. ng-model="filter.search"
      for (let property of _.keys(defaults).concat(properties)) {
         Object.defineProperty(this, property, {
            get: () => this._inputs[property],
            set: (value:any) => this.set(property, value)
         });
      }

      // debounce to avoid spamming callback when inputs change
      this.callback = _.debounce(this.callback, 100);
   }

   get inputs() {
      return this._inputs;
   }

   /**
    * Sets filter value
    * @param property
    * @param value
    */
   set(property:string, value:any) {
      // ignore null/empty/undefined on already undefined input
      // these values don't work well with backend in query parameters
      if (noValue(value) && noValue(this._inputs[property])) {
         return;
      }

      // use null/empty/undefined to clear existing inputs
      if (noValue(value)) {
         delete this._inputs[property];
      } else {
         this._inputs[property] = value;
      }

      // setting filters means the current page looses context, back to page 1
      // not certain this is the ideal place, if not move it to callbacks
      if (this._inputs['page'] && property !== 'page') {
         this._inputs['page'] = 1;
      }

      // give inputs object a new identity (new instance of object)
      // causes component bindings to inputs object to receive $onChanges() when
      // properties inside inputs object change
      this._inputs = ng.copy(this._inputs);

      this.localStorageService.set(this.key, this._inputs);
      this.callback(property);
   }

   reset() {
      for (let key in this._inputs) {
         if (this.defaults[key]) {
            this.set(key, this.defaults[key])
         } else {
            this.set(key, null);
         }
      }
   }

   sort(field:string) {
      if (this._inputs['orderBy'] === field) {
         // add a dash to the beginning of each value in field: -event.state.code,-event.cityName
         this.set('orderBy', _.map(field.split(','), key => '-' + key).join(','));
      } else {
         this.set('orderBy', field);
      }
   }

   sortDir(field:string) {
      if (!this._inputs['orderBy']) {
         return '';
      }

      // strip '-' so that we can test for combined fields (comma separated)
      if (this._inputs['orderBy'].replace(/-/g, '').indexOf(field) > -1) {
         if (this._inputs['orderBy'].indexOf('-') > -1) {
            return 'desc';
         }

         return 'asc';
      }

      return '';
   }
}

function noValue(value:any) {
   return value == null || value === '' || ng.isUndefined(value);
}

export interface FilterChangeCallback {
   (propertyName: string)
}

export interface FilterServiceFactory {
   (args:{
      /** Identifier used to store the filters */
      key:string,
      /** Set of default filter key/values */
      defaults:{},
      /** Additional filter keys that do not have a default */
      properties:string[],
      /** Callback function when one or more filters change */
      callback:FilterChangeCallback
   }):FilterService;
}
